/**
 * Admin Clients page — view + manage public trial claims.
 * Shows every MAC address that has claimed a free 5-day trial via the
 * public /trial page, plus its status (active / expired) and the key
 * that was issued.
 *
 * Admin actions:
 *   • Copy MAC / key
 *   • Release a MAC (disables the previous key and lets the client claim again)
 */
import React, { useEffect, useMemo, useState } from "react";
import { listTrials, deleteTrial } from "../lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import {
  UserCheck,
  RefreshCw,
  Copy,
  Search,
  Trash2,
  Clock,
  CheckCircle2,
  XCircle,
} from "lucide-react";

const formatMac = (raw) => {
  if (!raw) return "";
  const s = raw.toUpperCase();
  return s.length === 12 ? s.match(/.{1,2}/g).join(":") : s;
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

const StatusBadge = ({ r }) => {
  if (r.key_status === "disabled") {
    return <Badge className="bg-neutral-700 text-neutral-200"><XCircle className="mr-1 h-3 w-3" />Disabled</Badge>;
  }
  if (r.is_expired || r.key_status === "expired") {
    return <Badge className="bg-red-500/20 text-red-300"><Clock className="mr-1 h-3 w-3" />Expired</Badge>;
  }
  return (
    <Badge className="bg-emerald-500/20 text-emerald-300">
      <CheckCircle2 className="mr-1 h-3 w-3" />
      Active · {r.days_remaining}d left
    </Badge>
  );
};

export default function Clients() {
  const { toast } = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [releaseTarget, setReleaseTarget] = useState(null);
  const [releasing, setReleasing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await listTrials();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast({
        title: "Failed to load clients",
        description: e?.response?.data?.detail || e?.message || "Unknown error",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) =>
      (r.mac_address || "").toLowerCase().includes(needle) ||
      (r.key || "").toLowerCase().includes(needle) ||
      (r.ip || "").toLowerCase().includes(needle)
    );
  }, [rows, q]);

  const stats = useMemo(() => {
    const active = rows.filter((r) => !r.is_expired && r.key_status === "active").length;
    const expired = rows.filter((r) => r.is_expired || r.key_status === "expired").length;
    const disabled = rows.filter((r) => r.key_status === "disabled").length;
    return { total: rows.length, active, expired, disabled };
  }, [rows]);

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: `${label} copied`, description: text });
    } catch { /* silent */ }
  };

  const doRelease = async () => {
    if (!releaseTarget) return;
    setReleasing(true);
    try {
      await deleteTrial(releaseTarget.mac_address);
      toast({
        title: "Trial released",
        description: `${formatMac(releaseTarget.mac_address)} may claim again.`,
      });
      setReleaseTarget(null);
      await load();
    } catch (e) {
      toast({
        title: "Release failed",
        description: e?.response?.data?.detail || e?.message,
        variant: "destructive",
      });
    } finally {
      setReleasing(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-clients-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-3 text-2xl font-bold">
            <UserCheck className="h-7 w-7 text-emerald-400" />
            Clients
          </h1>
          <p className="text-sm text-neutral-400">
            Every MAC that has claimed a free 5-day trial via
            {" "}<code className="text-emerald-400">/trial</code>.
          </p>
        </div>
        <Button
          onClick={load}
          disabled={loading}
          variant="outline"
          data-testid="clients-refresh-btn"
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="clients-stats">
        <Card className="border-white/10 bg-neutral-900">
          <CardContent className="p-5">
            <div className="text-xs uppercase tracking-widest text-neutral-500">Total trials</div>
            <div className="mt-2 text-3xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>
        <Card className="border-emerald-500/20 bg-emerald-500/5">
          <CardContent className="p-5">
            <div className="text-xs uppercase tracking-widest text-emerald-400">Active</div>
            <div className="mt-2 text-3xl font-bold text-emerald-300">{stats.active}</div>
          </CardContent>
        </Card>
        <Card className="border-red-500/20 bg-red-500/5">
          <CardContent className="p-5">
            <div className="text-xs uppercase tracking-widest text-red-400">Expired</div>
            <div className="mt-2 text-3xl font-bold text-red-300">{stats.expired}</div>
          </CardContent>
        </Card>
        <Card className="border-white/10 bg-neutral-900">
          <CardContent className="p-5">
            <div className="text-xs uppercase tracking-widest text-neutral-500">Disabled</div>
            <div className="mt-2 text-3xl font-bold">{stats.disabled}</div>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card className="border-white/10 bg-neutral-900">
        <CardHeader className="flex flex-col gap-3 border-b border-white/5 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle className="text-base">All trial claims</CardTitle>
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-500" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search MAC, key or IP…"
              className="border-white/10 bg-neutral-950 pl-9 text-sm"
              data-testid="clients-search"
            />
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table data-testid="clients-table">
            <TableHeader>
              <TableRow className="border-white/5 hover:bg-transparent">
                <TableHead>MAC address</TableHead>
                <TableHead>Activation key</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Claimed</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead>Source IP</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="py-10 text-center text-neutral-500">
                    Loading…
                  </TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="py-10 text-center text-neutral-500">
                    {rows.length === 0
                      ? "No trials claimed yet. Share the /trial link with your customers."
                      : "No results for that filter."}
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((r) => (
                  <TableRow key={r.mac_address} className="border-white/5" data-testid={`client-row-${r.mac_address}`}>
                    <TableCell className="font-mono text-sm">{formatMac(r.mac_address)}</TableCell>
                    <TableCell className="font-mono text-sm text-emerald-300">{r.key}</TableCell>
                    <TableCell><StatusBadge r={r} /></TableCell>
                    <TableCell className="text-sm text-neutral-400">{formatDate(r.claimed_at)}</TableCell>
                    <TableCell className="text-sm text-neutral-400">{formatDate(r.expires_at)}</TableCell>
                    <TableCell className="text-sm text-neutral-400">{r.ip || "—"}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => copy(formatMac(r.mac_address), "MAC")}
                          data-testid={`client-copy-mac-${r.mac_address}`}
                          title="Copy MAC"
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => copy(r.key, "Key")}
                          data-testid={`client-copy-key-${r.mac_address}`}
                          title="Copy activation key"
                        >
                          <Copy className="h-4 w-4 text-emerald-400" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => setReleaseTarget(r)}
                          data-testid={`client-release-${r.mac_address}`}
                          title="Release MAC (allow re-claim)"
                        >
                          <Trash2 className="h-4 w-4 text-red-400" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Release confirmation */}
      <Dialog open={!!releaseTarget} onOpenChange={(open) => !open && setReleaseTarget(null)}>
        <DialogContent data-testid="release-trial-dialog">
          <DialogHeader>
            <DialogTitle>Release this MAC?</DialogTitle>
            <DialogDescription>
              This will disable the current activation key
              {" "}<code className="text-emerald-400">{releaseTarget?.key}</code> and
              remove {" "}<code>{formatMac(releaseTarget?.mac_address || "")}</code> from
              the block-list — the client can then claim a fresh 5-day trial from
              the public page.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setReleaseTarget(null)}
              data-testid="release-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={doRelease}
              disabled={releasing}
              className="bg-red-500 text-white hover:bg-red-600"
              data-testid="release-confirm"
            >
              {releasing ? "Releasing…" : "Release MAC"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
